// =====================================================================
// PLENARIUM:TV_SOBERANA -- documento (PDF) da materia no Painel TV
// PLENARIUM:PDF_CONTROLES_GRANDES -- controles do admin maiores (linha propria)
// PLENARIUM:PDF_ROLAR_TV -- no computador da TV, a rodinha do mouse e as teclas
// (setas, PageUp/PageDown, espaco, Home/End) rolam o documento. Os botoes do
// admin continuam mandando: cada clique la reposiciona a TV.
// PLENARIUM:PDF_TELA_CHEIA -- o documento ocupa a tela toda; o titulo da
// materia fica numa faixa no alto, na largura toda.
//
//  TV:    <MateriaTV dados={conducao.tela.dados} ... />  mostra a pagina
//         pedida pelo admin, grande e legivel: a pagina ocupa a largura
//         da tela (ou a pagina inteira) e o admin rola.
//  Admin: <BotaoPdfTV pauta sessaoId />     escolhe o documento e manda
//         <ControleMateriaTV ... />          pagina, rolagem, ajuste, tirar
//
//  dados: { pautaId, projetoId, doc ('principal' ou id do anexo), titulo,
//           numero, rotulo, pagina, parte, ajuste ('largura'|'pagina') }
//  O arquivo vem de /api/projetos-camara (so da propria camara).
// =====================================================================
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Tv, X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Maximize2, ZoomIn, FileText } from 'lucide-react';

const TIPO_ANEXO = { PROJETO: 'Projeto / texto', PARECER_COMISSAO: 'Parecer da Comissão', PARECER_RELATOR: 'Parecer do Relator', PARECER_JURIDICO: 'Parecer Jurídico', EMENDA: 'Emenda', SUBSTITUTIVO: 'Substitutivo', REDACAO_FINAL: 'Redação Final', OFICIO: 'Ofício', OUTROS: 'Documento' };
const PASSO = 0.55; // cada "rolar" anda 55% da altura visivel

let pdfjsPromessa = null;
function carregarPdfjs() {
  if (!pdfjsPromessa) {
    pdfjsPromessa = import('pdfjs-dist/legacy/build/pdf').then(m => {
      const lib = m.default && m.default.getDocument ? m.default : m;
      lib.GlobalWorkerOptions.workerSrc = (process.env.PUBLIC_URL || '') + '/pdf.worker.min.js';
      return lib;
    }).catch(e => { pdfjsPromessa = null; throw e; });
  }
  return pdfjsPromessa;
}
function urlDoc(d, camaraId) {
  const q = camaraId ? `?camaraId=${encodeURIComponent(camaraId)}` : '';
  return d.doc && d.doc !== 'principal' ? `/api/projetos-camara/anexo/${d.doc}${q}` : `/api/projetos-camara/${d.projetoId}/principal${q}`;
}
function bytesDe(dados) {
  const [cab, corpo] = dados.split(',');
  const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/pdf';
  const bin = atob(corpo); const b = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) b[i] = bin.charCodeAt(i);
  return { tipo, bytes: b };
}
// abre o documento (PDF ou imagem); guarda o ultimo aberto
const cacheDoc = { chave: null, prom: null };
function abrirDoc(d, headers, camaraId) {
  const chave = `${d.projetoId}|${d.doc || 'principal'}`;
  if (cacheDoc.chave === chave && cacheDoc.prom) return cacheDoc.prom;
  cacheDoc.chave = chave;
  cacheDoc.prom = (async () => {
    const r = await axios.get(urlDoc(d, camaraId), { headers: headers || {} });
    const dados = r.data && r.data.arquivo;
    if (!dados) throw new Error('Documento sem arquivo.');
    if (dados.startsWith('data:')) {
      const x = bytesDe(dados);
      if (x.tipo.startsWith('image/')) return { tipo: 'imagem', src: dados };
      if (x.tipo !== 'application/pdf') throw new Error('Este tipo de arquivo não pode ser mostrado na TV.');
      const lib = await carregarPdfjs();
      return { tipo: 'pdf', doc: await lib.getDocument({ data: x.bytes }).promise };
    }
    if (/\.(png|jpe?g|webp|gif)(\?|$)/i.test(dados)) return { tipo: 'imagem', src: dados };
    const lib = await carregarPdfjs();
    return { tipo: 'pdf', doc: await lib.getDocument({ url: dados }).promise };
  })();
  cacheDoc.prom.catch(() => { if (cacheDoc.chave === chave) { cacheDoc.chave = null; cacheDoc.prom = null; } });
  return cacheDoc.prom;
}

// ---------------------------------------------------------------- TV
const CSS_TV = `
.mtv { position: fixed; inset: 0; z-index: 900; display: flex; flex-direction: column; color: #fff; font-family: 'DM Sans', 'Inter', system-ui, sans-serif; background: #0b1f3d; }
.mtv-topo { flex: 0 0 auto; display: flex; align-items: center; gap: 2.4vh; padding: 1.4vh 2.4vh; background: linear-gradient(90deg, #071020 0%, #0d1f3c 100%); border-bottom: .4vh solid #b8862b; }
.mtv-topo img { height: 6.4vh; max-width: 7vh; object-fit: contain; flex-shrink: 0; }
.mtv-num { flex-shrink: 0; font-size: 3.6vh; font-weight: 900; line-height: 1; padding-right: 2.4vh; border-right: .2vh solid rgba(255,255,255,.2); white-space: nowrap; }
.mtv-num small { display: block; font-size: 1.3vh; letter-spacing: .3vh; text-transform: uppercase; color: #f5d78e; font-weight: 800; margin-bottom: .6vh; }
.mtv-tit { flex: 1; min-width: 0; font-size: 2.7vh; font-weight: 700; line-height: 1.25; color: rgba(255,255,255,.95); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.mtv-pag { flex-shrink: 0; text-align: right; padding-left: 2.4vh; border-left: .2vh solid rgba(255,255,255,.2); white-space: nowrap; }
.mtv-pag b { display: block; font-size: 3.2vh; font-weight: 900; line-height: 1; font-variant-numeric: tabular-nums; }
.mtv-pag span { display: block; font-size: 1.4vh; color: rgba(255,255,255,.6); font-weight: 700; margin-top: .5vh; max-width: 26vw; overflow: hidden; text-overflow: ellipsis; }
.mtv-folha { flex: 1; min-height: 0; position: relative; overflow: hidden; background: #fff; }
.mtv-mover { position: absolute; left: 0; right: 0; top: 0; display: flex; justify-content: center; transition: transform .6s cubic-bezier(.2,.8,.2,1); }
.mtv-folha.pagina { background: #e2e8f0; }
.mtv-mover canvas, .mtv-mover img { display: block; background: #fff; }
.mtv-msg { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; text-align: center; color: #334155; font-size: 3vh; font-weight: 700; padding: 6vh; }
.mtv-fim { position: absolute; left: 0; right: 0; bottom: 0; height: 5vh; background: linear-gradient(transparent, rgba(15,23,42,.28)); pointer-events: none; }
`;

export function MateriaTV({ dados, camara, headers, camaraId }) {
  const d = dados || {};
  const folhaRef = useRef(null);
  const moverRef = useRef(null);
  const [estado, setEstado] = useState({ fase: 'carregando' });
  const [tam, setTam] = useState({ w: 0, h: 0 });
  const [alturaConteudo, setAlturaConteudo] = useState(0);
  const geracao = useRef(0);
  const hdrs = useRef(headers); hdrs.current = headers;
  const [desvio, setDesvio] = useState(0); // PLENARIUM:PDF_ROLAR_TV posicao em pixels (rodinha do mouse)
  const [suave, setSuave] = useState(true);

  useEffect(() => {
    let vivo = true;
    setEstado({ fase: 'carregando' });
    abrirDoc(d, hdrs.current, camaraId).then(x => { if (vivo) setEstado({ fase: 'ok', ...x }); })
      .catch(e => { if (vivo) setEstado({ fase: 'erro', msg: (e && e.response && e.response.data && e.response.data.error) || (e && e.message) || 'Não foi possível abrir o documento.' }); });
    return () => { vivo = false; };
  }, [d.projetoId, d.doc, camaraId]);

  useEffect(() => {
    const medir = () => { const f = folhaRef.current; if (f) setTam({ w: f.clientWidth, h: f.clientHeight }); };
    medir(); window.addEventListener('resize', medir);
    return () => window.removeEventListener('resize', medir);
  }, []);

  const total = estado.fase === 'ok' && estado.tipo === 'pdf' ? estado.doc.numPages : 1;
  const pagina = Math.min(Math.max(1, d.pagina || 1), total);
  // desenha a pagina pedida
  useEffect(() => {
    if (estado.fase !== 'ok' || !tam.w || !moverRef.current) return;
    const caixa = moverRef.current; const minha = ++geracao.current;
    if (estado.tipo === 'imagem') {
      const img = new Image(); img.src = estado.src;
      img.onload = () => {
        if (minha !== geracao.current) return;
        const larg = d.ajuste === 'pagina' ? Math.min(tam.w, tam.h * img.width / img.height) : tam.w;
        img.style.width = larg + 'px'; img.style.height = 'auto';
        while (caixa.firstChild) caixa.removeChild(caixa.firstChild); caixa.appendChild(img);
        setAlturaConteudo(larg * img.height / img.width);
      };
      return;
    }
    (async () => {
      const pag = await estado.doc.getPage(pagina);
      const v1 = pag.getViewport({ scale: 1 });
      const larg = d.ajuste === 'pagina' ? Math.min(tam.w, tam.h * v1.width / v1.height) : tam.w;
      const escala = larg / v1.width; const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const vp = pag.getViewport({ scale: escala * dpr });
      const c = document.createElement('canvas');
      c.width = Math.floor(vp.width); c.height = Math.floor(vp.height);
      c.style.width = Math.floor(vp.width / dpr) + 'px'; c.style.height = Math.floor(vp.height / dpr) + 'px';
      await pag.render({ canvasContext: c.getContext('2d'), viewport: vp }).promise;
      if (minha !== geracao.current) return;
      while (caixa.firstChild) caixa.removeChild(caixa.firstChild); caixa.appendChild(c);
      setAlturaConteudo(vp.height / dpr);
    })().catch(() => { if (minha === geracao.current) setEstado({ fase: 'erro', msg: 'Não foi possível desenhar esta página.' }); });
  }, [estado, pagina, d.ajuste, tam.w, tam.h]);

  const sobra = Math.max(0, alturaConteudo - tam.h);
  // PLENARIUM:PDF_ROLAR_TV -- o admin manda a posicao (botoes); a rodinha/teclas do computador da TV ajustam dali
  useEffect(() => { setSuave(true); setDesvio((d.parte || 0) * tam.h * PASSO); }, [d.parte, pagina, d.ajuste, tam.h]);
  const sobraRef = useRef(0); sobraRef.current = sobra;
  useEffect(() => {
    const limitar = (v) => Math.min(Math.max(0, v), sobraRef.current);
    const rolar = (dy) => { setSuave(Math.abs(dy) > 200); setDesvio(v => limitar(Math.min(v, sobraRef.current) + dy)); };
    const roda = (e) => { e.preventDefault(); rolar(e.deltaMode === 1 ? e.deltaY * 40 : e.deltaY); };
    const tecla = (e) => {
      const h = (folhaRef.current && folhaRef.current.clientHeight) || 600;
      const mapa = { ArrowDown: 80, ArrowUp: -80, PageDown: h * 0.85, PageUp: -h * 0.85, ' ': h * 0.85 };
      if (mapa[e.key] !== undefined) { e.preventDefault(); rolar(mapa[e.key]); }
      else if (e.key === 'Home') { e.preventDefault(); setSuave(true); setDesvio(0); }
      else if (e.key === 'End') { e.preventDefault(); setSuave(true); setDesvio(sobraRef.current); }
    };
    window.addEventListener('wheel', roda, { passive: false }); window.addEventListener('keydown', tecla);
    return () => { window.removeEventListener('wheel', roda); window.removeEventListener('keydown', tecla); };
  }, []);
  const desloc = d.ajuste === 'pagina' ? 0 : Math.min(sobra, Math.max(0, desvio));
  return (
    <div className="mtv">
      <style>{CSS_TV}</style>
      <div className="mtv-topo">
        {camara && camara.logoUrl && <img src={camara.logoUrl} alt="" />}
        {d.numero && <div className="mtv-num"><small>Em apreciação</small>{d.numero}</div>}
        <div className="mtv-tit">{d.titulo || d.rotulo || 'Matéria'}</div>
        <div className="mtv-pag">
          {estado.fase === 'ok' && estado.tipo === 'pdf' ? <b>Pág. {pagina}/{total}</b> : null}
          <span>{d.rotulo || 'Texto da matéria'}</span>
        </div>
      </div>
      <div className={`mtv-folha ${d.ajuste === 'pagina' ? 'pagina' : ''}`} ref={folhaRef}>
        <div className="mtv-mover" ref={moverRef} style={{ transform: `translateY(${-desloc}px)`, transition: suave ? undefined : 'transform .12s ease-out' }} />
        {estado.fase === 'carregando' && <div className="mtv-msg">Abrindo o documento...</div>}
        {estado.fase === 'erro' && <div className="mtv-msg">{estado.msg}</div>}
        {estado.fase === 'ok' && d.ajuste !== 'pagina' && desloc < sobra - 2 && <div className="mtv-fim" />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- ADMIN
const gravarTela = (sessaoId, dados) => axios.put(`/api/conducao/sessao/${sessaoId}`, { tela: { modo: 'MATERIA', dados } });
const erroDe = (e, p) => (e && e.response && e.response.data && e.response.data.error) || p;

export function BotaoPdfTV({ pauta, sessaoId }) {
  const [aberto, setAberto] = useState(false);
  const [docs, setDocs] = useState(null);
  if (!pauta || !pauta.projetoId) return null;
  const mandar = async (doc, rotulo) => {
    try {
      await gravarTela(sessaoId, { pautaId: pauta.id, projetoId: pauta.projetoId, doc, rotulo, titulo: pauta.titulo || '', numero: pauta.numero || '', pagina: 1, parte: 0, ajuste: 'largura' });
      toast.success('Documento na TV. Use os controles na barra "Na TV".'); setAberto(false);
      try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { /* segue */ }
    } catch (e) { toast.error(erroDe(e, 'Não foi possível mostrar na TV.')); }
  };
  const abrir = async () => {
    setAberto(true); setDocs(null);
    try {
      const r = await axios.get(`/api/projetos-camara/${pauta.projetoId}/documentos`);
      const l = [];
      if (r.data.temPrincipal) l.push({ doc: 'principal', rotulo: 'Texto da matéria (cadastro)' });
      (r.data.anexos || []).forEach(a => l.push({ doc: a.id, rotulo: a.nome || TIPO_ANEXO[a.tipo] || 'Documento', tipo: TIPO_ANEXO[a.tipo] }));
      if (l.length === 1) { mandar(l[0].doc, l[0].rotulo); return; }
      setDocs(l);
    } catch (e) { toast.error(erroDe(e, 'Não foi possível listar os documentos.')); setAberto(false); }
  };
  return (
    <>
      <button type="button" className="btn btn-secondary btn-sm" onClick={abrir} title="Mostrar o documento desta matéria no Painel TV"><Tv size={13} /> PDF na TV</button>
      {aberto && docs && (
        <div className="modal-overlay" onClick={() => setAberto(false)}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title"><Tv size={16} /> Mostrar na TV — {pauta.numero}</h2>
              <button type="button" className="btn-modal-close" onClick={() => setAberto(false)}><X size={18} /></button>
            </div>
            {!docs.length ? <p style={{ color: '#64748b', fontSize: '.9rem' }}>Esta matéria não tem documento anexado. Anexe o PDF no Trâmite Legislativo.</p>
              : <>
                <p style={{ color: '#64748b', fontSize: '.85rem', margin: '0 0 10px' }}>Escolha o documento:</p>
                {docs.map(x => (
                  <button key={x.doc} type="button" className="btn btn-secondary" style={{ width: '100%', justifyContent: 'flex-start', marginBottom: 8, whiteSpace: 'normal', textAlign: 'left' }} onClick={() => mandar(x.doc, x.rotulo)}>
                    <FileText size={15} /> <span><b>{x.rotulo}</b>{x.tipo && x.tipo !== x.rotulo ? <small style={{ display: 'block', color: '#64748b', fontWeight: 500 }}>{x.tipo}</small> : null}</span>
                  </button>
                ))}
              </>}
            <div className="modal-actions"><button type="button" className="btn btn-secondary" onClick={() => setAberto(false)}>Fechar</button></div>
          </div>
        </div>
      )}
    </>
  );
}

const CSS_CTL = `
.mctl { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; flex-basis: 100%; padding: 8px 10px; border-radius: 12px; background: #ede9fe; border: 1px solid #c4b5fd; }
.mctl b { font-size: .95rem; color: #5b21b6; max-width: 340px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin: 0 6px; }
.mctl button { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 42px; min-width: 46px; border: 1px solid #a78bfa; background: #fff; color: #4c1d95; border-radius: 10px; padding: 6px 14px; font: inherit; font-size: .95rem; font-weight: 800; cursor: pointer; }
.mctl button:hover:not(:disabled) { background: #f5f3ff; }
.mctl button:disabled { opacity: .4; cursor: default; }
.mctl .pg { font-size: 1.05rem; font-weight: 900; color: #4c1d95; font-variant-numeric: tabular-nums; min-width: 70px; text-align: center; }
.mctl .sair { margin-left: auto; border-color: #fca5a5; color: #b91c1c; }
`;
export function ControleMateriaTV({ sessaoId, dados, onSair }) {
  const d = dados || {};
  const [total, setTotal] = useState(0);
  useEffect(() => {
    let vivo = true; setTotal(0);
    abrirDoc(d).then(x => { if (vivo) setTotal(x.tipo === 'pdf' ? x.doc.numPages : 1); }).catch(() => {});
    return () => { vivo = false; };
  }, [d.projetoId, d.doc]);
  const mudar = async (novo) => { try { await gravarTela(sessaoId, { ...d, ...novo }); } catch (e) { toast.error(erroDe(e, 'Não foi possível mudar a TV.')); } };
  const pag = d.pagina || 1;
  return (
    <span className="mctl">
      <style>{CSS_CTL}</style>
      <FileText size={18} color="#5b21b6" /><b title={`${d.numero || ''} — ${d.rotulo || ''}`}>{d.numero}{d.rotulo ? ` · ${d.rotulo}` : ''}</b>
      <button type="button" disabled={pag <= 1} onClick={() => mudar({ pagina: pag - 1, parte: 0 })} title="Página anterior"><ChevronLeft size={20} /></button>
      <span className="pg">{pag}{total ? ` / ${total}` : ''}</span>
      <button type="button" disabled={!!total && pag >= total} onClick={() => mudar({ pagina: pag + 1, parte: 0 })} title="Próxima página"><ChevronRight size={20} /></button>
      {d.ajuste !== 'pagina' && <>
        <button type="button" disabled={!(d.parte > 0)} onClick={() => mudar({ parte: Math.max(0, (d.parte || 0) - 1) })} title="Subir"><ChevronUp size={20} /></button>
        <button type="button" onClick={() => mudar({ parte: (d.parte || 0) + 1 })} title="Descer"><ChevronDown size={20} /></button>
      </>}
      <button type="button" onClick={() => mudar({ ajuste: d.ajuste === 'pagina' ? 'largura' : 'pagina', parte: 0 })} title={d.ajuste === 'pagina' ? 'Letra grande (largura da tela)' : 'Ver a página inteira'}>{d.ajuste === 'pagina' ? <><ZoomIn size={18} /> Letra grande</> : <><Maximize2 size={18} /> Página inteira</>}</button>
      <button type="button" className="sair" onClick={onSair} title="Tirar o documento da TV"><X size={18} /> Tirar da TV</button>
    </span>
  );
}
